import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react';
import authService from '../services/authService';

/**
 * SECURITY: Store CSRF token in document meta tag
 * This allows the token to be accessed by JS but not stored in localStorage
 */
const setCSRFToken = (token) => {
  if (!token) return;
  let meta = document.querySelector('meta[name="csrf-token"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'csrf-token';
    document.head.appendChild(meta);
  }
  meta.content = token;
};

const initialState = {
  user: null,
  accessToken: localStorage.getItem('accessToken'),
  // SECURITY: refreshToken is now handled via httpOnly cookie
  // We no longer store it in localStorage or state
  isAuthenticated: false,
  isLoading: true,
  permissions: {
    canViewB2BPricing: false,
    canAccessB2BFeatures: false,
    isAdmin: false,
  },
};

const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGOUT: 'LOGOUT',
  UPDATE_USER: 'UPDATE_USER',
  SET_PERMISSIONS: 'SET_PERMISSIONS',
  SET_TOKENS: 'SET_TOKENS',
};

const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        accessToken: action.payload.accessToken,
        // SECURITY: refreshToken handled via httpOnly cookie, not stored in state
        isAuthenticated: true,
        isLoading: false,
        permissions: action.payload.permissions || state.permissions,
      };
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...initialState,
        isLoading: false,
        accessToken: null,
      };
    case AUTH_ACTIONS.SET_TOKENS:
      return {
        ...state,
        accessToken: action.payload.accessToken,
        // SECURITY: refreshToken handled via httpOnly cookie
      };
    case AUTH_ACTIONS.UPDATE_USER:
      return { ...state, user: { ...state.user, ...action.payload } };
    case AUTH_ACTIONS.SET_PERMISSIONS:
      return { ...state, permissions: action.payload };
    default:
      return state;
  }
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const getUserRoleDisplayName = (role) => {
  const roleNames = {
    guest: 'Гость',
    b2c_customer: 'Покупатель',
    b2b_candidate: 'Заявка на B2B',
    b2b_verified: 'B2B Клиент',
    admin: 'Администратор',
  };
  return roleNames[role] || 'Неизвестно';
};

export const isB2BUser = (user) => {
  return (
    user && (user.role === 'b2b_verified' || user.role === 'b2b_candidate')
  );
};

export const hasB2BAccess = (user) => {
  return user && user.role === 'b2b_verified';
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const refreshTimeoutRef = useRef(null);

  const getTokenExpiry = useCallback((token) => {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  }, []);

  const scheduleRefresh = useCallback(
    (accessToken) => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      const exp = getTokenExpiry(accessToken);
      if (!exp) return;

      const now = Date.now();
      const msUntilRefresh = Math.max(exp - now - 90000, 10000);

      refreshTimeoutRef.current = setTimeout(async () => {
        try {
          // SECURITY: refreshToken is sent via httpOnly cookie automatically
          const res = await authService.refreshTokens();
          const newAccess = res; // refreshTokens returns the new access token directly
          localStorage.setItem('accessToken', newAccess);
          authService.setTokens(newAccess);

          dispatch({
            type: AUTH_ACTIONS.SET_TOKENS,
            payload: { accessToken: newAccess },
          });

          scheduleRefresh(newAccess);
        } catch {
          localStorage.removeItem('accessToken');
          authService.clearTokens();
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      }, msUntilRefresh);
    },
    [getTokenExpiry]
  );

  useEffect(() => {
    const initializeAuth = async () => {
      const storedAccess = localStorage.getItem('accessToken');
      // SECURITY: refreshToken is now handled via httpOnly cookie

      if (!storedAccess) {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        return;
      }

      try {
        authService.setTokens(storedAccess);
        const response = await Promise.race([
          authService.getCurrentUser(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Auth timeout')), 10000)
          ),
        ]);
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: response.data.user,
            accessToken: storedAccess,
            permissions: response.data.permissions,
          },
        });
        scheduleRefresh(storedAccess);
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        localStorage.removeItem('accessToken');
        authService.clearTokens();
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    };

    initializeAuth();
    return () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    };
  }, [scheduleRefresh]);

  useEffect(() => {
    if (state.accessToken) {
      // SECURITY: refreshToken is handled via httpOnly cookie
      scheduleRefresh(state.accessToken);
    } else if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
  }, [state.accessToken, scheduleRefresh]);

  const login = async (email, password) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      const delayMs = parseInt(import.meta.env.VITE_FAKE_DELAY_MS || '0', 10);
      if (delayMs > 0) {
        await new Promise((res) => setTimeout(res, delayMs));
      }
      const response = await authService.login(email, password);
      const { accessToken, csrfToken, user, permissions } = response.data;

      // SECURITY: Store only accessToken in localStorage
      // refreshToken is handled via httpOnly cookie
      localStorage.setItem('accessToken', accessToken);
      authService.setTokens(accessToken);

      // SECURITY: Store CSRF token in meta tag for protection against CSRF attacks
      if (csrfToken) {
        setCSRFToken(csrfToken);
      }

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, accessToken, permissions },
      });
      scheduleRefresh(accessToken);
      return { success: true, user };
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      const message =
        error?.response?.data?.error ||
        error?.message ||
        'Authentication failed';
      throw new Error(message);
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      const response = await authService.register(userData);
      const { accessToken, csrfToken, user, permissions } = response.data;

      // SECURITY: Store only accessToken in localStorage
      // refreshToken is handled via httpOnly cookie
      localStorage.setItem('accessToken', accessToken);
      authService.setTokens(accessToken);

      // SECURITY: Store CSRF token in meta tag
      if (csrfToken) {
        setCSRFToken(csrfToken);
      }

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: {
          user,
          accessToken,
          permissions,
        },
      });
      scheduleRefresh(accessToken);
      return { success: true, user };
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Server will clear httpOnly cookies
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // SECURITY: Only clear accessToken from localStorage
      // refreshToken is cleared by server via cookie
      localStorage.removeItem('accessToken');
      authService.clearTokens();
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  // Apply for B2B status
  const applyForB2B = async (companyData) => {
    const response = await authService.applyForB2B(companyData);

    // Update user role to b2b_candidate
    dispatch({
      type: AUTH_ACTIONS.UPDATE_USER,
      payload: { role: 'b2b_candidate' },
    });

    return response.data;
  };

  // Update profile
  const updateProfile = async (profileData) => {
    const response = await authService.updateProfile(profileData);

    dispatch({
      type: AUTH_ACTIONS.UPDATE_USER,
      payload: response.data.user,
    });

    return response.data;
  };

  // Set user data locally (without server request)
  const setUser = (userData) => {
    dispatch({
      type: AUTH_ACTIONS.UPDATE_USER,
      payload: userData,
    });
  };

  // Check if user can access B2B features
  const canAccessB2BFeatures = () => {
    return state.permissions.canAccessB2BFeatures || hasB2BAccess(state.user);
  };

  // Check if user can view B2B pricing
  const canViewB2BPricing = () => {
    return state.permissions.canViewB2BPricing || hasB2BAccess(state.user);
  };

  // Check if user is admin
  const isAdmin = () => {
    return (
      state.permissions.isAdmin || (state.user && state.user.role === 'admin')
    );
  };

  // Get user type for UI customization
  const getUserType = () => {
    if (!state.user) return 'guest';
    if (hasB2BAccess(state.user)) return 'b2b';
    if (isB2BUser(state.user)) return 'b2b_candidate';
    return 'b2c';
  };

  const value = {
    // State
    user: state.user,
    accessToken: state.accessToken,
    // SECURITY: refreshToken not exposed - handled via httpOnly cookie
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    permissions: state.permissions,

    // Actions
    login,
    register,
    logout,
    applyForB2B,
    updateProfile,
    setUser,

    // Helper functions
    canAccessB2BFeatures,
    canViewB2BPricing,
    isAdmin,
    getUserType,
    getUserRoleDisplayName: () => getUserRoleDisplayName(state.user?.role),
    isB2BUser: () => isB2BUser(state.user),
    hasB2BAccess: () => hasB2BAccess(state.user),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
